import { cloneDeep } from 'lodash';
import { format } from 'date-fns';
import { DateRangeValue, DateRangeValueJson, FixedDateRange, IGlobalFiltersWrapper, IQueryFilterWrapper, LegacyDateRangeValueJson, Model, parseDate, Query, QueryFilter, Schema } from '@hub-components';
import { Table } from './table.model';

export type Settings<DateRangeValueType> = {
  interval?: DateRangeValueType,
};

export interface ReportJson {
  account: string;
  name: string
  public_uuid: string
  settings: Settings<DateRangeValueJson | LegacyDateRangeValueJson>
  tables: any[]
  is_template: boolean;
  share_uuid?: string;
  owner?: string;
  editors?: string[];
  created_at?: Date;
  updated_at?: Date;
  global_filters: IGlobalFiltersWrapper;
}

export class Report extends Model {
  public name: string;
  public owner: string;
  public account: string;
  public editors: string[];
  public tables: Table[];
  public settings: Settings<DateRangeValue> & { legacyInterval?: LegacyDateRangeValueJson };
  public shareUuid: string;
  public publicUuid: string;
  public permissions: any;
  public isTemplate: boolean;
  public hasEditorPermission: boolean;
  public reportId: number;
  public favoriteId: number;
  public globalFilters: IGlobalFiltersWrapper = { version: 1, data: [] };
  public schemas: Record<string, Schema> = {};

  constructor(obj: Partial<ReportJson> = {}, schemas: Record<string, Schema> = {} ) {
    super(obj);
    this.schemas = schemas;
    if (obj.settings) {
      if (obj.settings.interval) {
        const interval = this.parseDateRangeValue(obj.settings.interval);
        if (interval) {
          this.settings.interval = interval;
        } else {
          this.settings.legacyInterval = <LegacyDateRangeValueJson> obj.settings.interval;
          delete this.settings.interval;
        }
      }
    } else {
      this.settings = {};
    }
    let tableNameOccurrences = {};
    this.tables = (this.tables || []).map((t) => {
      const schema = schemas[t?.query?.dataset];
      const query = new Query(t['query'], schema);
      query.auth = t.query.auth;
      const options = t.options;
      if (options?.dateOptions?.interval) {
        options.dateOptions.interval = this.parseDateRangeValue(options.dateOptions.interval as (DateRangeValueJson | LegacyDateRangeValueJson));
      }

      let tableName = t['name'] && t['name'].substring(0, 31);
      tableNameOccurrences[tableName] = (tableNameOccurrences[tableName] || 0) + 1;
      if (tableNameOccurrences[tableName] > 1) {
        return new Table(
          (`${tableName.substring(0, 29 - (tableNameOccurrences[tableName] - 1).toString().length)}(${tableNameOccurrences[tableName] - 1})`),
          query,
          options,
        );
      } else {
        return new Table(tableName, query, options);
      }
    });

    if (obj.global_filters) {
      this.globalFilters = this.processFilters(obj.global_filters, schemas);
    }
  }

  public addTable() {
    const currentTableNamesNumber = this.tables.filter(table => table.name.includes('Table ')).map(table => parseInt(table.name.replace('Table ', '')));
    const tableNumber = currentTableNamesNumber.length ? Math.max(...currentTableNamesNumber) + 1 : 1;
    const name = `Table ${tableNumber}`;

    this.tables.push(
      new Table(
        name,
        new Query({ interval: this.tables[0] && this.tables[0].query.interval }),
      ),
    );
  }

  public duplicateTable(index: number) {
    const tableToDuplicate = this.tables[index];
    const baseName = tableToDuplicate.name.replace(/\(\d+\)$/, '');
    let highestNumber = 0;

    this.tables.forEach((table) => {
      const match = table.name.match(new RegExp(`^${baseName}\\((\\d+)\\)$`));
      if (match) {
        const number = parseInt(match[1], 10);
        if (number > highestNumber) {
          highestNumber = number;
        }
      }
    });
    const newNumber = highestNumber + 1;
    const newName = `${baseName}(${newNumber})`;

    const duplicatedTable = new Table(
      newName,
      tableToDuplicate.query.clone(),
      cloneDeep(tableToDuplicate.options),
    );
    this.tables.splice(index + 1, 0, duplicatedTable);
  }

  public getCompactedDatasets() {
    let compactedDatasets = new Set();
    for (let t of this.tables) {
      if (t.query.wasCompacted) {
        compactedDatasets.add(t.query.schema.displayName);
      }
    }
    return compactedDatasets;
  }

  public hasGlobalFilters() {
    return this.globalFilters?.data?.some(filter => !!filter.expressions.length);
  }

  public isValid() {
    return this.tables.map(t => t.query).every(q => q.isValid());
  }

  public asJson(): ReportJson {
    return {
      account: this.account,
      name: this.name,
      public_uuid: this.publicUuid,
      settings: {
        ...this.settings,
        interval: this.jsonDateRangeValue(this.settings.interval),
      },
      tables: this.tables.map(t => {
        if (!!t.options?.dateOptions?.interval) {
          t.options.dateOptions.interval = this.jsonDateRangeValue(t.options.dateOptions.interval) as any;
        }
        return t.asJson();
      }),
      is_template: this.isTemplate,
      global_filters: this.formatFilters(this.globalFilters),
    };
  }

  private parseDateRangeValue(range: DateRangeValueJson | LegacyDateRangeValueJson): DateRangeValue {
    if ('dynamic' in range) {
      return {
        type: 'dynamic',
        dynamic: range['dynamic'],
      };
    } else if (range.type === 'dynamic') {
      const match = (range['value'] || '').match(/^(this|last|yesterday)(?:(?:_(\d+))?_(day|week|month|year)s?)?$/);
      if (match) {
        return {
          type: 'dynamic',
          dynamic: { name: match[1], num: match[2] && +match[2], unit: <any> match[3] },
        };
      }
    } else if ('start_date' in range) {
      const startDate = range.start_date;
      const endDate = range.end_date;
      return {
        type: 'fixed',
        startDate: parseDate(startDate),
        endDate: parseDate(endDate),
      };
    } else {
      const [startDate, endDate] = range['value'].split('-', 2);
      return {
        type: 'fixed',
        startDate: parseDate(startDate),
        endDate: parseDate(endDate),
      };
    }
  }

  private jsonDateRangeValue(range: DateRangeValue): DateRangeValueJson & LegacyDateRangeValueJson {
    if ('dynamic' in range && range['dynamic']) {
      const { name, num, unit } = range['dynamic'];
      let value = name;
      if (num && num > 1) {
        value += `_${num}`;
      }
      if (unit) {
        value += `_${unit}`;
        if (num && num > 1) {
          value += 's';
        }
      }
      return {
        type: 'dynamic',
        value: value,
        dynamic: range['dynamic'],
      };
    } else {
      const { startDate, endDate } = <FixedDateRange> range;
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      return {
        type: 'fixed',
        value: `${startDateStr.replaceAll('-', '')}-${endDateStr.replaceAll('-', '')}`,
        start_date: startDateStr,
        end_date: endDateStr,
      };
    }
  }

  private processFilters(globalFilters: IGlobalFiltersWrapper, schemas: Record<string, Schema>) {
    if (!globalFilters?.data) {
      return globalFilters;
    }

    const data = globalFilters.data.map((filter: IQueryFilterWrapper) => {
      const filterSchema = schemas[filter.dataset];
      const processedFilter: IQueryFilterWrapper = {
        expressions: filter.expressions.map(expression => {
          if (!expression.dimension && filterSchema) {
            const dimension = filterSchema.dimensions.find(dim => dim.name === expression.key);
            return new QueryFilter({ ...expression, dimension });
          } else {
            return new QueryFilter(expression);
          }
        }),
      };
      processedFilter.dataset = filter.dataset;
      return processedFilter;
    }).filter(filter => filter.expressions.length);
    return { version: globalFilters.version, data };
  }

  private formatFilters(globalFilters): IGlobalFiltersWrapper {
    if (!globalFilters?.data) {
      return globalFilters;
    }

    const data = globalFilters.data.map((filter: IQueryFilterWrapper) => {
      return { dataset: filter.dataset, expressions: filter.expressions.map(expression => expression.asJson()) };
    });

    return { version: globalFilters.version, data };
  }
}
